import { useEffect, useRef, useState, useCallback } from 'react';
import { useAppDispatch, useAppSelector, type RootState } from '@/store';
import { Send, Loader2, Sparkle, BookCopy } from 'lucide-react';
import { addMessage } from './messageSlice';
import { sendMessage } from '@/mainApi';

export default function MessageInput() {
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state: RootState) => state.user.userId);
  const chatId = useAppSelector((state: RootState) => state.messages.chatId);

  const [userText, setUserText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [defaultThinking, setDefaultThinking] = useState<boolean>(true);
  const [defaultContext, setDefaultContext] = useState<boolean>(true);

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const resize = () => {
      const h = el.offsetHeight;
      document.documentElement.style.setProperty('--input-height', `${h}px`);
    };

    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(el);

    return () => obs.disconnect();
  }, []);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const adjustHeight = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  }, []);
  const handleToggleThinking = () => setDefaultThinking((value) => !value);
  const handleToggleContext = () => setDefaultContext((value) => !value);

  const handleSend = async () => {
    if (!userText.trim() || isLoading) return;

    const text = userText;
    setUserText('');
    textareaRef.current!.style.height = 'auto';

    dispatch(addMessage({ role: 'user', text }));

    setIsLoading(true);
    try {
      const msg = await sendMessage(userId, chatId, text, defaultThinking, defaultContext);
      dispatch(addMessage(msg));
    } catch (error) {
      console.error('Error sending message:', error);
      dispatch(addMessage({
        text: 'Our server is having a moment. Please try to rephrase your statement.', // or restart a new session.',
        role: 'system',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div ref={containerRef} className="sticky bottom-0 bg-white border-t border-slate-200 p-3 shadow-sm w-full">
      <div className="mx-auto max-w-screen-sm flex gap-2 items-end">

        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleToggleThinking}
            aria-pressed={defaultThinking}
            title={defaultThinking ? 'Thinking: Default' : 'Thinking: Long'}
            className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              defaultThinking
                ? 'bg-slate-200 hover:bg-slate-400 text-slate-700 disabled:bg-slate-100 disabled:text-slate-400'
                : 'bg-blue-600 hover:bg-blue-800 text-white disabled:bg-blue-500'
            } disabled:cursor-not-allowed`}
            disabled={isLoading}
          >
            <Sparkle size={14} />
            <span className="sm:inline">{defaultThinking ? 'Default' : 'Long'}</span>
          </button>
          <button
            type="button"
            onClick={handleToggleContext}
            aria-pressed={defaultContext}
            title={defaultContext ? 'Context: Data Only' : 'Context: More'}
            className={`flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              defaultContext
                ? 'bg-slate-200 hover:bg-slate-400 text-slate-700 disabled:bg-slate-100 disabled:text-slate-400'
                : 'bg-blue-600 hover:bg-blue-800 text-white disabled:bg-blue-500'
            } disabled:cursor-not-allowed`}
            disabled={isLoading}
          >
            <BookCopy size={14} />
            <span className="sm:inline">{defaultContext ? 'Data Only' : 'More'}</span>
          </button>
        </div>
        <textarea
          ref={textareaRef}
          value={userText}
          onChange={(e) => {
            setUserText(e.target.value);
            adjustHeight();
          }}
          className="flex-1 min-h-[40px] max-h-[200px] px-3 py-2 border border-slate-300 rounded-md resize-none overflow-hidden"
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.ctrlKey && !isLoading) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Ask about SM Malls Online data."
          aria-label="Message input (Ctrl+Enter to send)"
        />
        <button 
          onClick={handleSend}
          disabled={!userText.trim() || isLoading}
          className="h-10 w-10 flex items-center justify-center p-3 rounded-xl bg-blue-600 hover:bg-blue-800 text-white"
        >
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        </button>
      </div>
    </div>
  );
}
